import React, { useState, useEffect } from "react";
import { ChevronDown, MessageCircle, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSiteContext } from "../../../../context/SiteContext";
import requests from "../../../../services/httpServices";

const faqItems = [
  { question: "How do I join my meeting room?", answer: "Click the 'Join Room' button on your My Room page. Your room is always available at your unique room link." },
  { question: "How do I share my room link with others?", answer: "Use the 'Copy Room Link' button on your My Room page to copy your unique meeting URL." },
  { question: "What happens if I run out of storage?", answer: "Your room will be flagged and new recordings may be paused. Contact support or delete old recordings to free up space." },
  { question: "How do I update my billing information?", answer: "Go to the Billing page and click 'Update Payment Method' to update your card on file." },
];

const mapTicket = (t) => ({
  id: t?.id,
  subject: t?.subject || "",
  category: t?.category || "",
  message: t?.body || "",
  status: t?.status === "resolved" ? "Resolved" : t?.admin_reply ? "Responded" : "Open",
  date: t?.created_at
    ? new Date(t.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "",
});

export default function MemberSupport() {
  const { user } = useSiteContext();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [formState, setFormState] = useState({ subject: "", category: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState("");
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState("");
  const [expandedFaq, setExpandedFaq] = useState(null);
  const memberId = user?.memberId;

  useEffect(() => {
    if (!memberId) return;
    let isMounted = true;
    const loadTickets = async () => {
      setTicketsLoading(true);
      setTicketsError("");
      try {
        const data = await requests.get(`/member/${memberId}/support-tickets`);
        if (data?.success === false) throw new Error(data?.message || "Failed to load tickets.");
        if (isMounted) setTickets(Array.isArray(data?.tickets) ? data.tickets.map(mapTicket) : []);
      } catch (err) {
        if (isMounted) setTicketsError(err?.message || "Failed to load tickets.");
      } finally {
        if (isMounted) setTicketsLoading(false);
      }
    };
    loadTickets();
    return () => { isMounted = false; };
  }, [memberId]);

  useEffect(() => {
    if (!memberId) return;
    let isMounted = true;
    const loadCategories = async () => {
      setCategoriesLoading(true);
      setCategoriesError("");
      try {
        const data = await requests.get(`/member/${memberId}/support-categories`);
        if (data?.success === false) throw new Error(data?.message || "Failed to load categories.");
        const list = Array.isArray(data?.categories)
          ? data.categories.filter((i) => i !== null && i !== undefined && String(i).trim() !== "").map((i) => String(i))
          : [];
        if (!isMounted) return;
        setCategories(list);
        setFormState((prev) => ({ ...prev, category: prev.category && list.includes(prev.category) ? prev.category : list[0] || "" }));
      } catch (err) {
        if (isMounted) {
          setCategories([]);
          setCategoriesError(err?.message || "Failed to load categories.");
          setFormState((prev) => ({ ...prev, category: "" }));
        }
      } finally {
        if (isMounted) setCategoriesLoading(false);
      }
    };
    loadCategories();
    return () => { isMounted = false; };
  }, [memberId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!memberId) return;
    if (!formState.category) { setTicketsError("Please select a category."); return; }
    setLoading(true);
    try {
      const data = await requests.post(`/member/${memberId}/support-tickets`, {
        subject: formState.subject,
        body: formState.message,
        category: formState.category,
      });
      if (data?.success === false) throw new Error(data?.message || "Failed to submit ticket.");
      const ticket = data?.ticket || {};
      setTickets((prev) => [
        { ...mapTicket({ ...ticket, subject: ticket.subject || formState.subject, body: ticket.body || formState.message, category: ticket.category || formState.category }), status: "Open" },
        ...prev,
      ]);
      setSubmitted(true);
    } catch (err) {
      setTicketsError(err?.message || "Failed to submit ticket.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormState({ subject: "", category: categories[0] || "", message: "" });
    setSubmitted(false);
  };

  if (!memberId) return <div style={{ padding: 40, color: "rgba(255,255,255,0.4)" }}>Member account required.</div>;

  return (
    <div style={{ padding: "8px 16px 40px", fontFamily: "'DM Sans', sans-serif", color: "#ffffff" }}>
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)", marginBottom: 8 }}>SUPPORT</div>
        <h1 style={{ fontSize: 30, fontWeight: 800, color: "#ffffff", margin: "0 0 8px 0" }}>Support</h1>
        <p style={{ fontSize: 15, color: "rgba(255,255,255,0.6)", margin: 0 }}>Get help with your room, billing, or account.</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 24 }}>
        <div style={card}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: "#ffffff", margin: "0 0 20px" }}>Submit a Support Ticket</h3>
          {!submitted ? (
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <Field label="Subject">
                <input
                  type="text"
                  placeholder="Brief description of your issue"
                  value={formState.subject}
                  onChange={(e) => setFormState({ ...formState, subject: e.target.value })}
                  required
                  style={input}
                />
              </Field>
              <Field label="Category">
                <select
                  value={formState.category}
                  onChange={(e) => setFormState({ ...formState, category: e.target.value })}
                  disabled={categoriesLoading || categories.length === 0}
                  style={input}
                >
                  {categories.length === 0 ? (
                    <option value="" disabled>
                      {categoriesLoading ? "Loading categories..." : categoriesError ? "Categories unavailable" : "No categories available"}
                    </option>
                  ) : (
                    categories.map((c) => <option key={c} value={c}>{c}</option>)
                  )}
                </select>
                {categoriesError && <div style={{ fontSize: 12, color: "#ef4444", marginTop: 4 }}>{categoriesError}</div>}
              </Field>
              <Field label="Message">
                <textarea
                  placeholder="Describe your issue in detail."
                  rows={5}
                  value={formState.message}
                  onChange={(e) => setFormState({ ...formState, message: e.target.value })}
                  required
                  style={{ ...input, resize: "vertical" }}
                />
              </Field>
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: "12px 16px", borderRadius: 8, border: "none",
                  background: loading ? "rgba(106,31,191,0.5)" : "linear-gradient(135deg, #6a1fbf, #a64dff)",
                  color: "#ffffff", fontSize: 14, fontWeight: 600,
                  cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? "Submitting..." : "Submit Ticket"}
              </button>
            </form>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "32px 24px", textAlign: "center" }}>
              <CheckCircle2 size={48} style={{ color: "#00c853", marginBottom: 16 }} />
              <div style={{ fontSize: 18, fontWeight: 700, color: "#00c853", marginBottom: 8 }}>Ticket Submitted!</div>
              <div style={{ fontSize: 14, color: "rgba(255,255,255,0.6)", marginBottom: 20 }}>We'll respond within 24 hours at {user?.email}</div>
              <button onClick={resetForm} style={{ padding: "10px 16px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", background: "transparent", color: "rgba(255,255,255,0.6)", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>
                Submit Another Ticket
              </button>
            </div>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          <div style={card}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: "#ffffff", margin: "0 0 16px" }}>My Tickets</h3>
            {ticketsError && <div style={{ marginBottom: 12, fontSize: 12, color: "#ef4444" }}>{ticketsError}</div>}
            {ticketsLoading ? (
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }}>Loading tickets...</div>
            ) : tickets.length === 0 ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "32px 16px", textAlign: "center" }}>
                <MessageCircle size={32} style={{ color: "rgba(255,255,255,0.2)", marginBottom: 12 }} />
                <div style={{ fontSize: 14, fontWeight: 600, color: "rgba(255,255,255,0.5)", marginBottom: 4 }}>No tickets yet</div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)" }}>Your submitted tickets will appear here.</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {tickets.map((ticket) => (
                  <button
                    key={ticket.id}
                    onClick={() => navigate(`/dashboard/support/ticket/${encodeURIComponent(ticket.id)}`)}
                    style={{ padding: 12, borderRadius: 8, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", textAlign: "left", cursor: "pointer", color: "#ffffff" }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#ffffff", marginBottom: 6 }}>{ticket.subject}</div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
                      <span style={{ padding: "3px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600, background: "rgba(106,31,191,0.15)", color: "#a64dff" }}>{ticket.category}</span>
                      <span style={{ padding: "3px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600, background: "rgba(250,204,21,0.15)", color: "#f59e0b" }}>{ticket.status}</span>
                    </div>
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)" }}>{ticket.date}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={card}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: "#ffffff", margin: "0 0 16px" }}>Common Questions</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {faqItems.map((item, idx) => (
                <div key={idx}>
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                    style={{ width: "100%", padding: "12px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", color: "#ffffff" }}
                  >
                    <span style={{ fontSize: 14, fontWeight: 600, color: "#ffffff", textAlign: "left" }}>{item.question}</span>
                    <ChevronDown size={16} style={{ color: "rgba(255,255,255,0.4)", transform: expandedFaq === idx ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease", flexShrink: 0, marginLeft: 12 }} />
                  </button>
                  {expandedFaq === idx && (
                    <div style={{ padding: "12px 14px", fontSize: 14, color: "rgba(255,255,255,0.6)", background: "rgba(255,255,255,0.01)", borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                      {item.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)" }}>{label}</label>
      {children}
    </div>
  );
}

const card = {
  padding: 24,
  borderRadius: 14,
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(255,255,255,0.08)",
};

const input = {
  padding: "10px 14px",
  borderRadius: 8,
  border: "1px solid rgba(255,255,255,0.1)",
  background: "rgba(255,255,255,0.03)",
  color: "#ffffff",
  fontSize: 14,
  fontFamily: "'DM Sans', sans-serif",
  width: "100%",
  boxSizing: "border-box",
};
